/**
 * Navigation Single Source of Truth (SSOT)
 * 
 * This is the canonical data structure for all navigation maps in EzERP.
 * All visual maps, sitemaps, menu trees, and breadcrumb rules are derived from this SSOT.
 * 
 * DO NOT duplicate navigation data elsewhere - always derive from this structure.
 */

export const NODE_TYPES = {
  PAGE: 'page',
  SECTION: 'section',
  GROUP: 'group',
  WIZARD: 'wizard',
  MODAL: 'modal',
  EXTERNAL_LINK: 'external_link'
};

export const MODULE_OWNERS = {
  ADMIN: 'Admin',
  ERP: 'ERP',
  EZPARCEL: 'EzParcel',
  WMS: 'WMS',
  FINANCE: 'Finance',
  PROCUREMENT: 'Procurement',
  CRM: 'CRM',
  COMMUNICATIONS: 'Communications',
  OIG: 'OIG',
  CALENDAR: 'Calendar',
  EZTASK: 'EzTask',
  ECOMMERCE: 'Ecommerce',
  CNS: 'CNS',
  REPORTS: 'Reports',
  HR: 'Human Resources',
  ACCOUNTING: 'Accounting',
  FISCAL: 'Fiscal Engine'
};

export const PERMISSION_ROLES = {
  PLATFORM_ADMIN: 'platform_admin',
  CLIENT_ADMIN: 'client_admin',
  MANAGER: 'manager',
  OPERATOR: 'operator',
  DRIVER: 'driver',
  WAREHOUSE: 'warehouse',
  FINANCE: 'finance',
  VIEWER: 'viewer'
};

// Tooltip dictionary for UI terms
export const TOOLTIP_DICTIONARY = {
  'RBAC': 'Role-Based Access Control - Users see only pages they have permission to access based on their assigned roles.',
  'Module Owner': 'The functional domain or team responsible for this section of the application.',
  'Node Type': 'Classification of the navigation element: page (full view), section (sub-area), group (logical container), wizard (multi-step flow), modal (overlay dialog), or external_link.',
  'Visible in Menu': 'Whether this item appears in the sidebar navigation menu. Some pages exist but are only accessible via direct links or actions.',
  'SSOT': 'Single Source of Truth - This canonical data structure from which all navigation views are derived.',
  'Breadcrumb': 'The hierarchical path showing where you are in the application, e.g., Home > WMS > Inventory.',
  'Click Path': 'The sequence of navigation actions to reach a specific page or feature.',
  'Multi-Tenant': 'Architecture where a single application instance serves multiple isolated customer organizations.'
};

/**
 * Navigation SSOT Structure
 * Each node follows this schema:
 * - id: stable unique identifier
 * - title: menu label / page name
 * - route: URL path (e.g., /wms/inbound)
 * - type: page | section | group | wizard | modal | external_link
 * - module_owner: responsible module
 * - description: 1-3 line description
 * - key_features: array of feature strings
 * - permissions_required: array of role strings
 * - visible_in_menu: boolean
 * - menu_parent_id: nullable parent reference
 * - children: array of child node ids
 * - links_to: array of related node ids (cross-navigation)
 * - tags: array of classification strings
 * - status: active | planned | deprecated
 * - tooltip_text: short hover description
 * - breadcrumb_label: optional override for breadcrumb display
 * - breadcrumb_hidden_parent_ids: optional array of parent ids to skip in breadcrumb
 * - icon: optional lucide icon name
 * - badge: optional badge text (e.g., "New", "Beta")
 * - order: display order within parent (lower = first)
 */

export const NAVIGATION_SSOT = {
  // Root node
  'root': {
    id: 'root',
    title: 'EzERP',
    route: '/',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.ERP,
    description: 'Enterprise Resource Planning Platform',
    key_features: ['Multi-tenant', 'Role-based access', 'Real-time analytics'],
    permissions_required: [],
    visible_in_menu: false,
    menu_parent_id: null,
    children: ['erp-dashboard', 'ezparcel', 'wms', 'finance', 'procurement', 'crm', 'communications', 'oig', 'calendar', 'eztask', 'ecommerce', 'cns', 'reports', 'hr', 'accounting', 'fiscal', 'admin'],
    links_to: [],
    tags: ['root', 'navigation'],
    status: 'active',
    tooltip_text: 'Main application root',
    order: 0
  },

  // ERP Dashboard
  'erp-dashboard': {
    id: 'erp-dashboard',
    title: 'ERP Dashboard',
    route: '/',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ERP,
    description: 'Cross-module overview with key metrics, quick actions, and unified analytics across all ERP modules.',
    key_features: ['Module health status', 'Quick statistics', 'Activity feed', 'Cross-module search'],
    permissions_required: [PERMISSION_ROLES.VIEWER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: [],
    links_to: ['ezparcel-dashboard', 'wms-dashboard', 'finance-dashboard'],
    tags: ['overview', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Central dashboard with cross-module analytics',
    icon: 'LayoutGrid',
    order: 1
  },

  // EzParcel Module (Logistics)
  'ezparcel': {
    id: 'ezparcel',
    title: 'Logistics & Delivery',
    route: '/ezparcel',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Complete parcel logistics and last-mile delivery management system.',
    key_features: ['Shipment tracking', 'Route optimization', 'Fleet management', 'Driver apps'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['ezparcel-dashboard', 'ezparcel-fleet', 'ezparcel-shipments', 'ezparcel-routes', 'ezparcel-drivers', 'ezparcel-vehicles', 'ezparcel-warehouses', 'ezparcel-customers', 'ezparcel-billing', 'ezparcel-documents'],
    links_to: ['wms', 'finance'],
    tags: ['logistics', 'delivery', 'operations'],
    status: 'active',
    tooltip_text: 'Manage shipments, routes, and delivery operations',
    icon: 'Truck',
    badge: 'Active',
    order: 2
  },

  'ezparcel-dashboard': {
    id: 'ezparcel-dashboard',
    title: 'Logistics Dashboard',
    route: '/ezparcel/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Real-time logistics overview with shipment metrics, delivery performance, and fleet status.',
    key_features: ['Live shipment map', 'Delivery KPIs', 'Driver status', 'Today\'s schedule'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-shipments', 'ezparcel-routes'],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Logistics performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'ezparcel-fleet': {
    id: 'ezparcel-fleet',
    title: 'Fleet Management',
    route: '/fleet',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Manage your entire delivery fleet including vehicles, maintenance schedules, and fuel tracking.',
    key_features: ['Vehicle registry', 'Maintenance alerts', 'Fuel tracking', 'Insurance management'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-vehicles', 'ezparcel-drivers'],
    tags: ['fleet', 'vehicles', 'setup'],
    status: 'active',
    tooltip_text: 'Fleet vehicles and maintenance',
    icon: 'Truck',
    order: 2
  },

  'ezparcel-shipments': {
    id: 'ezparcel-shipments',
    title: 'Shipments',
    route: '/shipments',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Create, track, and manage all shipments across the delivery network.',
    key_features: ['Shipment creation', 'Bulk import', 'Status tracking', 'Proof of delivery'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-routes', 'ezparcel-customers'],
    tags: ['shipments', 'tracking', 'daily'],
    status: 'active',
    tooltip_text: 'Manage all shipments',
    icon: 'Package',
    order: 3
  },

  'ezparcel-routes': {
    id: 'ezparcel-routes',
    title: 'Routes',
    route: '/routes',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Plan and optimize delivery routes with AI-powered suggestions.',
    key_features: ['Route planning', 'AI optimization', 'Time windows', 'Zone management'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-drivers', 'ezparcel-shipments'],
    tags: ['routes', 'optimization', 'daily'],
    status: 'active',
    tooltip_text: 'Plan and optimize routes',
    icon: 'Route',
    order: 4
  },

  'ezparcel-drivers': {
    id: 'ezparcel-drivers',
    title: 'Drivers',
    route: '/drivers',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Manage driver profiles, availability, certifications, and performance.',
    key_features: ['Driver profiles', 'Availability calendar', 'Performance metrics', 'Document management'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-vehicles', 'ezparcel-routes'],
    tags: ['drivers', 'hr', 'setup'],
    status: 'active',
    tooltip_text: 'Driver management',
    icon: 'Users',
    order: 5
  },

  'ezparcel-vehicles': {
    id: 'ezparcel-vehicles',
    title: 'Vehicles',
    route: '/vehicles',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Vehicle inventory, specifications, and assignment to drivers.',
    key_features: ['Vehicle registry', 'Capacity specs', 'Assignment history', 'Status tracking'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['ezparcel-fleet', 'ezparcel-drivers'],
    tags: ['vehicles', 'fleet', 'setup'],
    status: 'active',
    tooltip_text: 'Vehicle inventory',
    icon: 'Car',
    order: 6
  },

  'ezparcel-warehouses': {
    id: 'ezparcel-warehouses',
    title: 'Warehouses',
    route: '/warehouses',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Manage warehouse locations for distribution and last-mile operations.',
    key_features: ['Location management', 'Capacity planning', 'Zone mapping', 'Operating hours'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['wms'],
    tags: ['warehouses', 'locations', 'setup'],
    status: 'active',
    tooltip_text: 'Warehouse locations',
    icon: 'Warehouse',
    order: 7
  },

  'ezparcel-customers': {
    id: 'ezparcel-customers',
    title: 'Customers',
    route: '/customers',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Customer database with shipping preferences and address book.',
    key_features: ['Customer profiles', 'Address management', 'Shipping preferences', 'History'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['crm', 'ezparcel-shipments'],
    tags: ['customers', 'crm', 'daily'],
    status: 'active',
    tooltip_text: 'Customer database',
    icon: 'Users',
    order: 8
  },

  'ezparcel-billing': {
    id: 'ezparcel-billing',
    title: 'Billing',
    route: '/billing',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Invoice generation, pricing rules, and payment tracking for logistics services.',
    key_features: ['Invoice generation', 'Pricing tables', 'Payment tracking', 'COD management'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: ['finance'],
    tags: ['billing', 'finance', 'reporting'],
    status: 'active',
    tooltip_text: 'Logistics billing',
    icon: 'Receipt',
    order: 9
  },

  'ezparcel-documents': {
    id: 'ezparcel-documents',
    title: 'Documents',
    route: '/documents',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZPARCEL,
    description: 'Document templates and generation for shipping labels, manifests, and reports.',
    key_features: ['Template management', 'Label generation', 'Manifest creation', 'Report export'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ezparcel',
    children: [],
    links_to: [],
    tags: ['documents', 'templates', 'reporting'],
    status: 'active',
    tooltip_text: 'Shipping documents',
    icon: 'FileText',
    order: 10
  },

  // WMS Module
  'wms': {
    id: 'wms',
    title: 'Warehouse Management',
    route: '/wms',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Complete warehouse operations management including inventory, receiving, picking, and shipping.',
    key_features: ['Inventory management', 'Pick/Pack/Ship', 'Barcode scanning', 'Location management'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['wms-dashboard', 'wms-inventory', 'wms-products', 'wms-receiving', 'wms-putaway', 'wms-picking', 'wms-packing', 'wms-pallets', 'wms-expedition'],
    links_to: ['ezparcel'],
    tags: ['warehouse', 'inventory', 'operations'],
    status: 'active',
    tooltip_text: 'Warehouse operations and inventory',
    icon: 'Warehouse',
    badge: 'Active',
    order: 3
  },

  'wms-dashboard': {
    id: 'wms-dashboard',
    title: 'WMS Dashboard',
    route: '/wms/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Warehouse operations dashboard with KPIs, task queues, and real-time activity.',
    key_features: ['Task queue', 'Inventory alerts', 'Performance KPIs', 'Activity feed'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-inventory', 'wms-picking'],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Warehouse performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'wms-inventory': {
    id: 'wms-inventory',
    title: 'Inventory',
    route: '/wms/inventory',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Complete inventory management with stock levels, locations, and movement history.',
    key_features: ['Stock levels', 'Location mapping', 'Movement history', 'Cycle counting'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-products'],
    tags: ['inventory', 'stock', 'daily'],
    status: 'active',
    tooltip_text: 'Inventory stock levels',
    icon: 'Package',
    order: 2
  },

  'wms-products': {
    id: 'wms-products',
    title: 'Products',
    route: '/wms/products',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Product master data including SKUs, dimensions, and handling requirements.',
    key_features: ['Product catalog', 'SKU management', 'Dimensions', 'Storage requirements'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-inventory'],
    tags: ['products', 'master-data', 'setup'],
    status: 'active',
    tooltip_text: 'Product master data',
    icon: 'Box',
    order: 3
  },

  'wms-receiving': {
    id: 'wms-receiving',
    title: 'Receiving',
    route: '/wms/receiving',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Inbound goods receiving with ASN processing and quality inspection.',
    key_features: ['ASN receiving', 'Quality inspection', 'Blind receiving', 'Exception handling'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-putaway', 'wms-inventory'],
    tags: ['receiving', 'inbound', 'daily'],
    status: 'active',
    tooltip_text: 'Receive incoming goods',
    icon: 'Download',
    order: 4
  },

  'wms-putaway': {
    id: 'wms-putaway',
    title: 'Putaway',
    route: '/wms/putaway',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Directed putaway with optimized location suggestions.',
    key_features: ['Location suggestions', 'Task assignment', 'Mobile scanning', 'Confirmation'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-receiving', 'wms-inventory'],
    tags: ['putaway', 'inbound', 'daily'],
    status: 'active',
    tooltip_text: 'Put received goods to locations',
    icon: 'ArrowDownToLine',
    order: 5
  },

  'wms-picking': {
    id: 'wms-picking',
    title: 'Picking',
    route: '/wms/picking',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Order picking with wave management and optimized pick paths.',
    key_features: ['Wave picking', 'Batch picking', 'Pick path optimization', 'Zone picking'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-packing'],
    tags: ['picking', 'outbound', 'daily'],
    status: 'active',
    tooltip_text: 'Pick orders for shipping',
    icon: 'ClipboardList',
    order: 6
  },

  'wms-packing': {
    id: 'wms-packing',
    title: 'Packing',
    route: '/wms/packing',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Packing station for order verification and shipping label generation.',
    key_features: ['Pack verification', 'Carton selection', 'Label printing', 'Packing slip'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-picking', 'wms-expedition'],
    tags: ['packing', 'outbound', 'daily'],
    status: 'active',
    tooltip_text: 'Pack picked orders',
    icon: 'Package',
    order: 7
  },

  'wms-pallets': {
    id: 'wms-pallets',
    title: 'Pallets',
    route: '/wms/pallets',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Pallet management for bulk storage and handling.',
    key_features: ['Pallet tracking', 'Consolidation', 'Deconsolidation', 'Label management'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['wms-inventory'],
    tags: ['pallets', 'storage', 'daily'],
    status: 'active',
    tooltip_text: 'Pallet operations',
    icon: 'Layers',
    order: 8
  },

  'wms-expedition': {
    id: 'wms-expedition',
    title: 'Expedition',
    route: '/wms/expedition',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.WMS,
    description: 'Shipping dock management and carrier hand-off.',
    key_features: ['Dock scheduling', 'Carrier assignment', 'Load verification', 'Ship confirmation'],
    permissions_required: [PERMISSION_ROLES.WAREHOUSE],
    visible_in_menu: true,
    menu_parent_id: 'wms',
    children: [],
    links_to: ['ezparcel-shipments'],
    tags: ['shipping', 'outbound', 'daily'],
    status: 'active',
    tooltip_text: 'Ship orders to carriers',
    icon: 'Send',
    order: 9
  },

  // Finance Module
  'finance': {
    id: 'finance',
    title: 'Financial Management',
    route: '/finance',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Complete financial operations including accounting, banking, and reporting.',
    key_features: ['General ledger', 'Bank feeds', 'Cash flow', 'Financial reports'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['finance-dashboard', 'finance-ledger', 'finance-payable', 'finance-receivable', 'finance-banking', 'finance-reports', 'finance-budgets'],
    links_to: ['ezparcel-billing'],
    tags: ['finance', 'accounting', 'operations'],
    status: 'active',
    tooltip_text: 'Financial operations and reporting',
    icon: 'DollarSign',
    badge: 'Active',
    order: 4
  },

  'finance-dashboard': {
    id: 'finance-dashboard',
    title: 'Finance Dashboard',
    route: '/finance/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Financial overview with key metrics, cash position, and aging reports.',
    key_features: ['Cash position', 'Revenue trends', 'Expense analysis', 'Aging summary'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: ['finance-reports'],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Financial performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'finance-ledger': {
    id: 'finance-ledger',
    title: 'General Ledger',
    route: '/finance/general-ledger',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Chart of accounts and journal entries management.',
    key_features: ['Chart of accounts', 'Journal entries', 'Trial balance', 'Account reconciliation'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: [],
    tags: ['accounting', 'ledger', 'daily'],
    status: 'active',
    tooltip_text: 'General ledger and accounts',
    icon: 'BookOpen',
    order: 2
  },

  'finance-payable': {
    id: 'finance-payable',
    title: 'Accounts Payable',
    route: '/finance/accounts-payable',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Vendor invoice management and payment processing.',
    key_features: ['Invoice entry', 'Payment scheduling', 'Aging report', 'Vendor management'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: ['finance-banking'],
    tags: ['payable', 'vendors', 'daily'],
    status: 'active',
    tooltip_text: 'Manage vendor payments',
    icon: 'CreditCard',
    order: 3
  },

  'finance-receivable': {
    id: 'finance-receivable',
    title: 'Accounts Receivable',
    route: '/finance/accounts-receivable',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Customer invoicing and payment collection.',
    key_features: ['Invoice generation', 'Payment tracking', 'Aging report', 'Customer statements'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: ['finance-banking'],
    tags: ['receivable', 'customers', 'daily'],
    status: 'active',
    tooltip_text: 'Manage customer receivables',
    icon: 'Receipt',
    order: 4
  },

  'finance-banking': {
    id: 'finance-banking',
    title: 'Banking',
    route: '/finance/banking',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Bank account management with Open Finance integration for automatic feeds.',
    key_features: ['Bank feeds', 'Transaction matching', 'Reconciliation', 'Cash management'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: [],
    tags: ['banking', 'reconciliation', 'daily'],
    status: 'active',
    tooltip_text: 'Bank accounts and feeds',
    icon: 'Landmark',
    order: 5
  },

  'finance-reports': {
    id: 'finance-reports',
    title: 'Financial Reports',
    route: '/finance/reports',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Financial statements, cash flow analysis, and custom report builder.',
    key_features: ['Balance sheet', 'Income statement', 'Cash flow', 'Custom reports'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: ['reports'],
    tags: ['reports', 'statements', 'reporting'],
    status: 'active',
    tooltip_text: 'Financial statements and reports',
    icon: 'FileText',
    order: 6
  },

  'finance-budgets': {
    id: 'finance-budgets',
    title: 'Budget Management',
    route: '/finance/budgets',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FINANCE,
    description: 'Budget planning, tracking, and variance analysis.',
    key_features: ['Budget creation', 'Variance analysis', 'Forecasting', 'Department budgets'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'finance',
    children: [],
    links_to: ['finance-reports'],
    tags: ['budgets', 'planning', 'reporting'],
    status: 'active',
    tooltip_text: 'Budget planning and tracking',
    icon: 'Calculator',
    order: 7
  },

  // Procurement Module
  'procurement': {
    id: 'procurement',
    title: 'Procurement',
    route: '/procurement',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.PROCUREMENT,
    description: 'Purchase order management and supplier relations.',
    key_features: ['Purchase orders', 'Supplier management', 'RFQ process', 'Contract management'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['procurement-dashboard', 'procurement-orders', 'procurement-suppliers'],
    links_to: ['wms', 'finance'],
    tags: ['procurement', 'purchasing', 'operations'],
    status: 'active',
    tooltip_text: 'Purchasing and supplier management',
    icon: 'ShoppingCart',
    order: 5
  },

  'procurement-dashboard': {
    id: 'procurement-dashboard',
    title: 'Procurement Dashboard',
    route: '/procurement/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.PROCUREMENT,
    description: 'Procurement overview with pending orders and supplier metrics.',
    key_features: ['Order status', 'Spend analysis', 'Supplier performance', 'Pending approvals'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'procurement',
    children: [],
    links_to: [],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Procurement performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'procurement-orders': {
    id: 'procurement-orders',
    title: 'Purchase Orders',
    route: '/procurement/orders',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.PROCUREMENT,
    description: 'Create and manage purchase orders with approval workflows.',
    key_features: ['Order creation', 'Approval workflow', 'Order tracking', 'Receipt matching'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'procurement',
    children: [],
    links_to: ['wms-receiving'],
    tags: ['orders', 'purchasing', 'daily'],
    status: 'active',
    tooltip_text: 'Manage purchase orders',
    icon: 'ClipboardList',
    order: 2
  },

  'procurement-suppliers': {
    id: 'procurement-suppliers',
    title: 'Suppliers',
    route: '/procurement/suppliers',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.PROCUREMENT,
    description: 'Supplier database with performance ratings and contact management.',
    key_features: ['Supplier profiles', 'Performance ratings', 'Contract tracking', 'Communication history'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'procurement',
    children: [],
    links_to: [],
    tags: ['suppliers', 'vendors', 'setup'],
    status: 'active',
    tooltip_text: 'Manage suppliers',
    icon: 'Users',
    order: 3
  },

  // CRM Module
  'crm': {
    id: 'crm',
    title: 'CRM',
    route: '/crm',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Customer relationship management with leads, opportunities, prospect discovery, campaign tracking, orchestration, and publisher/portals syndication.',
    key_features: ['Lead management', 'Opportunity tracking', 'Prospect Discovery', 'Campaign Intelligence', 'Orchestration Center', 'Portal Manager', 'Publishing Console', 'Ad Connectors', 'Customer support', 'Sales pipeline'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['crm-dashboard', 'crm-leads', 'crm-opportunities', 'crm-contacts', 'crm-prospect-discovery', 'crm-campaign-intelligence', 'crm-marketing-hub', 'crm-portal-manager', 'crm-orchestration', 'crm-brand-protection', 'crm-publishing-console', 'crm-ad-connectors', 'crm-presentation-hub'],
    links_to: ['ezparcel-customers'],
    tags: ['crm', 'sales', 'operations', 'syndication', 'advertising'],
    status: 'active',
    tooltip_text: 'Customer relationships and sales',
    icon: 'Users',
    order: 6
  },

  'crm-marketing-hub': {
    id: 'crm-marketing-hub',
    title: 'Sales & Marketing Hub',
    route: '/crm/marketing-hub',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Central library for sales enablement, marketing materials, and campaign assets.',
    key_features: ['Sales materials', 'Partner kits', 'Pitch decks', 'Outreach templates', 'Contract templates'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: [],
    tags: ['sales', 'marketing', 'enablement', 'assets'],
    status: 'active',
    tooltip_text: 'Central library for sales materials',
    icon: 'Library',
    order: 6.5
  },

  'crm-dashboard': {
    id: 'crm-dashboard',
    title: 'CRM Dashboard',
    route: '/crm/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Sales pipeline overview with lead metrics and conversion rates.',
    key_features: ['Pipeline view', 'Lead metrics', 'Conversion rates', 'Team performance'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: [],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'CRM performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'crm-leads': {
    id: 'crm-leads',
    title: 'Leads',
    route: '/crm/leads',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Lead capture, qualification, conversion tracking, and database-backed Persona Profiles with persistent images and Extra Notes.',
    key_features: ['Lead capture', 'Scoring', 'Assignment', 'Conversion tracking', 'Persona Profiles', 'Persistent Profile Images', 'Persistent Extra Notes'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-opportunities'],
    tags: ['leads', 'sales', 'daily'],
    status: 'active',
    tooltip_text: 'Manage sales leads',
    icon: 'Target',
    order: 2
  },

  'crm-opportunities': {
    id: 'crm-opportunities',
    title: 'Opportunities',
    route: '/crm/opportunities',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Sales opportunity management with stage tracking and forecasting.',
    key_features: ['Pipeline stages', 'Deal tracking', 'Win/loss analysis', 'Revenue forecast'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: [],
    tags: ['opportunities', 'sales', 'daily'],
    status: 'active',
    tooltip_text: 'Track sales opportunities',
    icon: 'TrendingUp',
    order: 3
  },

  'crm-contacts': {
    id: 'crm-contacts',
    title: 'Contacts',
    route: '/crm/contacts',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Contact database with communication history and relationship mapping.',
    key_features: ['Contact profiles', 'Communication log', 'Organization mapping', 'Activity timeline'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: [],
    tags: ['contacts', 'relationships', 'daily'],
    status: 'active',
    tooltip_text: 'Manage contacts',
    icon: 'Users',
    order: 4
  },

  'crm-prospect-discovery': {
    id: 'crm-prospect-discovery',
    title: 'Prospect Discovery',
    route: '/crm/prospect-discovery',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Automated web scraping and AI-powered prospect enrichment for lead generation.',
    key_features: ['Web scraping', 'AI enrichment', 'Validation queue', 'Industry filtering', 'Multi-source data'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-leads', 'crm-campaign-intelligence'],
    tags: ['prospect', 'discovery', 'automation', 'ai', 'scraping'],
    status: 'active',
    tooltip_text: 'Automated prospect discovery with AI',
    icon: 'Target',
    order: 5
  },

  'crm-campaign-intelligence': {
    id: 'crm-campaign-intelligence',
    title: 'Campaign Intelligence',
    route: '/crm/campaign-intelligence',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Campaign tracking, analytics, and visitor correlation across marketing channels.',
    key_features: ['Tracking links', 'Website analytics', 'Visitor journey', 'UTM tracking', 'Conversion tracking'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-prospect-discovery', 'crm-leads'],
    tags: ['campaign', 'intelligence', 'analytics', 'tracking', 'marketing'],
    status: 'active',
    tooltip_text: 'Campaign tracking and analytics',
    icon: 'TrendingUp',
    order: 6
  },

  'crm-portal-manager': {
    id: 'crm-portal-manager',
    title: 'Portal Manager',
    route: '/crm/portal-manager',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Manage publisher portals, ad placements, and creative specifications for syndication campaigns.',
    key_features: ['Portal registry', 'Placement catalog', 'Spec sets', 'Rate cards', 'Mode A/B/C support'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-publishing-console', 'crm-ad-connectors'],
    tags: ['portals', 'placements', 'syndication', 'advertising', 'publisher'],
    status: 'active',
    tooltip_text: 'Manage publisher portals and ad placements',
    icon: 'Globe',
    order: 7
  },

  'crm-orchestration': {
    id: 'crm-orchestration',
    title: 'Orchestration Center',
    route: '/crm/orchestration',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Native CRM control plane for UTM/session intake, event routing, destination delivery, ROI sync, and diagnostics.',
    key_features: ['UTM intake', 'Route builder', 'Destination health', 'Server-side conversion delivery', 'ROI reconciliation', 'Replay diagnostics'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-campaign-intelligence', 'crm-ad-connectors', 'crm-publishing-console', 'crm-portal-manager'],
    tags: ['orchestration', 'campaign', 'tracking', 'utm', 'roi', 'server-side', 'diagnostics'],
    status: 'active',
    tooltip_text: 'Route campaign and CRM events to internal and external destinations',
    icon: 'GitBranch',
    order: 7.5
  },

  'crm-brand-protection': {
    id: 'crm-brand-protection',
    title: 'Brand Protection',
    route: '/crm/brand-protection',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'CRM-native brand identity and username reservation tracker for WhatsApp-first protection workflows.',
    key_features: ['WhatsApp username checklist', 'Manual verification statuses', 'Owner and MFA tracking', 'Audit history', 'Exportable checklist'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-marketing-hub', 'crm-orchestration'],
    tags: ['brand', 'identity', 'whatsapp', 'social', 'audit'],
    status: 'active',
    tooltip_text: 'Track brand username reservation status and evidence',
    icon: 'ShieldCheck',
    order: 7.75
  },

  'crm-publishing-console': {
    id: 'crm-publishing-console',
    title: 'Publishing',
    route: '/crm/publishing-console',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Monitor and manage campaign publishing jobs with state machine workflows and DLQ.',
    key_features: ['Publish jobs', 'Schedule management', 'State machine', 'DLQ replay', 'Mode A/B/C publishing'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-portal-manager', 'crm-ad-connectors'],
    tags: ['publishing', 'jobs', 'scheduling', 'workflow', 'automation'],
    status: 'active',
    tooltip_text: 'Manage campaign publishing jobs',
    icon: 'Send',
    order: 8
  },

  'crm-ad-connectors': {
    id: 'crm-ad-connectors',
    title: 'Connectors',
    route: '/crm/ad-connectors',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Configure and manage ad network connectors for automated Mode A publishing.',
    key_features: ['Google Ads', 'Meta Ads', 'OAuth management', 'Health monitoring', 'Rate limit tracking'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-portal-manager', 'crm-publishing-console'],
    tags: ['connectors', 'google ads', 'meta ads', 'integration', 'oauth'],
    status: 'active',
    tooltip_text: 'Manage ad network API connectors',
    icon: 'Wifi',
    order: 9
  },

  'crm-presentation-hub': {
    id: 'crm-presentation-hub',
    title: 'The Presentation HUB',
    route: '/crm/presentation-hub',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CRM,
    description: 'Native CRM control surface for public EzERP, partner, Emissor, and campaign presentations.',
    key_features: ['Public presentation slugs', 'Presentation Hub service status', 'NGINX alias mapping', 'Admin access guardrails', 'Deployment readiness'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'crm',
    children: [],
    links_to: ['crm-marketing-hub', 'crm-orchestration', 'crm-campaign-intelligence'],
    tags: ['presentations', 'sales', 'partners', 'public', 'deployment'],
    status: 'active',
    tooltip_text: 'Manage public presentation URLs and hub readiness',
    icon: 'Presentation',
    order: 10
  },

  // Communications Module
  'communications': {
    id: 'communications',
    title: 'Communications',
    route: '/communications',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.COMMUNICATIONS,
    description: 'Unified communications including messaging, notifications, and announcements.',
    key_features: ['Messaging', 'Notifications', 'Announcements', 'Communication templates'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['communications-inbox', 'communications-notifications'],
    links_to: [],
    tags: ['communications', 'messaging', 'operations'],
    status: 'active',
    tooltip_text: 'Internal communications',
    icon: 'MessageSquare',
    order: 7
  },

  'communications-inbox': {
    id: 'communications-inbox',
    title: 'Inbox',
    route: '/communications/inbox',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.COMMUNICATIONS,
    description: 'Unified inbox for all internal and external communications.',
    key_features: ['Message threads', 'Filtering', 'Search', 'Attachments'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'communications',
    children: [],
    links_to: [],
    tags: ['inbox', 'messages', 'daily'],
    status: 'active',
    tooltip_text: 'Message inbox',
    icon: 'Inbox',
    order: 1
  },

  'communications-notifications': {
    id: 'communications-notifications',
    title: 'Notifications',
    route: '/notifications',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.COMMUNICATIONS,
    description: 'Notification center for system alerts and updates.',
    key_features: ['System alerts', 'User notifications', 'Preferences', 'History'],
    permissions_required: [PERMISSION_ROLES.VIEWER],
    visible_in_menu: true,
    menu_parent_id: 'communications',
    children: [],
    links_to: [],
    tags: ['notifications', 'alerts', 'daily'],
    status: 'active',
    tooltip_text: 'View notifications',
    icon: 'Bell',
    order: 2
  },

  // OIG Module (Operations Intelligence)
  'oig': {
    id: 'oig',
    title: 'OIG Analytics',
    route: '/oig',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.OIG,
    description: 'Operations Intelligence Group - Advanced analytics and business intelligence.',
    key_features: ['Analytics dashboards', 'Custom reports', 'Data visualization', 'KPI tracking'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['oig-dashboard', 'oig-analytics'],
    links_to: [],
    tags: ['analytics', 'intelligence', 'reporting'],
    status: 'active',
    tooltip_text: 'Business intelligence',
    icon: 'BarChart3',
    order: 8
  },

  'oig-dashboard': {
    id: 'oig-dashboard',
    title: 'OIG Dashboard',
    route: '/oig/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.OIG,
    description: 'Executive dashboard with cross-module KPIs and trend analysis.',
    key_features: ['Executive KPIs', 'Trend analysis', 'Benchmarking', 'Alert summary'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'oig',
    children: [],
    links_to: [],
    tags: ['dashboard', 'executive', 'daily'],
    status: 'active',
    tooltip_text: 'Executive KPI dashboard',
    icon: 'LayoutDashboard',
    order: 1
  },

  'oig-analytics': {
    id: 'oig-analytics',
    title: 'Analytics',
    route: '/oig/analytics',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.OIG,
    description: 'Deep-dive analytics with custom visualizations and data exploration.',
    key_features: ['Custom charts', 'Data exploration', 'Export capabilities', 'Saved views'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'oig',
    children: [],
    links_to: [],
    tags: ['analytics', 'visualization', 'reporting'],
    status: 'active',
    tooltip_text: 'Advanced analytics',
    icon: 'BarChart3',
    order: 2
  },

  // Calendar Module
  'calendar': {
    id: 'calendar',
    title: 'Calendar',
    route: '/calendar',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.CALENDAR,
    description: 'Enterprise calendar with scheduling, event management, and integrations.',
    key_features: ['Event scheduling', 'Resource booking', 'Reminders', 'External sync'],
    permissions_required: [PERMISSION_ROLES.VIEWER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['calendar-main'],
    links_to: [],
    tags: ['calendar', 'scheduling', 'daily'],
    status: 'active',
    tooltip_text: 'Calendar and scheduling',
    icon: 'Calendar',
    order: 9
  },

  'calendar-main': {
    id: 'calendar-main',
    title: 'Calendar View',
    route: '/calendar',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CALENDAR,
    description: 'Main calendar view with day, week, month, and agenda views.',
    key_features: ['Multiple views', 'Event creation', 'Drag-and-drop', 'Color coding'],
    permissions_required: [PERMISSION_ROLES.VIEWER],
    visible_in_menu: true,
    menu_parent_id: 'calendar',
    children: [],
    links_to: [],
    tags: ['calendar', 'events', 'daily'],
    status: 'active',
    tooltip_text: 'View and manage calendar',
    icon: 'Calendar',
    order: 1
  },

  // EzTask Module (Kanban)
  'eztask': {
    id: 'eztask',
    title: 'EzTask',
    route: '/eztask',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.EZTASK,
    description: 'Kanban-based project and task management system.',
    key_features: ['Kanban boards', 'Task management', 'Team collaboration', 'Due dates'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['eztask-boards'],
    links_to: ['calendar'],
    tags: ['tasks', 'projects', 'daily'],
    status: 'active',
    tooltip_text: 'Task and project management',
    icon: 'KanbanSquare',
    order: 10
  },

  'eztask-boards': {
    id: 'eztask-boards',
    title: 'Task Boards',
    route: '/eztask/boards',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.EZTASK,
    description: 'Kanban boards for visual task management and workflow tracking.',
    key_features: ['Board creation', 'Task cards', 'Drag-and-drop', 'Swimlanes'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'eztask',
    children: [],
    links_to: [],
    tags: ['kanban', 'boards', 'daily'],
    status: 'active',
    tooltip_text: 'Manage task boards',
    icon: 'KanbanSquare',
    order: 1
  },

  // Ecommerce Module
  'ecommerce': {
    id: 'ecommerce',
    title: 'Ecommerce',
    route: '/ecommerce',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.ECOMMERCE,
    description: 'Complete ecommerce platform with storefront builder and payment processing.',
    key_features: ['Storefront builder', 'Stripe payments', 'Order management', 'Product catalog'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['ecommerce-dashboard', 'ecommerce-products', 'ecommerce-orders', 'ecommerce-storefronts'],
    links_to: ['wms', 'finance'],
    tags: ['ecommerce', 'sales', 'operations'],
    status: 'active',
    tooltip_text: 'Online store management',
    icon: 'Store',
    badge: 'New',
    order: 11
  },

  'ecommerce-dashboard': {
    id: 'ecommerce-dashboard',
    title: 'Ecommerce Dashboard',
    route: '/ecommerce/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ECOMMERCE,
    description: 'Sales overview with revenue metrics, order status, and conversion rates.',
    key_features: ['Sales metrics', 'Order status', 'Conversion tracking', 'Revenue trends'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ecommerce',
    children: [],
    links_to: [],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Ecommerce performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'ecommerce-products': {
    id: 'ecommerce-products',
    title: 'Products',
    route: '/ecommerce/products',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ECOMMERCE,
    description: 'Product catalog management with pricing, images, and inventory sync.',
    key_features: ['Product catalog', 'Pricing', 'Images', 'Categories'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ecommerce',
    children: [],
    links_to: ['wms-products'],
    tags: ['products', 'catalog', 'setup'],
    status: 'active',
    tooltip_text: 'Manage product catalog',
    icon: 'Package',
    order: 2
  },

  'ecommerce-orders': {
    id: 'ecommerce-orders',
    title: 'Orders',
    route: '/ecommerce/orders',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ECOMMERCE,
    description: 'Online order management with fulfillment tracking and customer communication.',
    key_features: ['Order list', 'Fulfillment', 'Customer notes', 'Refunds'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'ecommerce',
    children: [],
    links_to: ['wms-picking'],
    tags: ['orders', 'fulfillment', 'daily'],
    status: 'active',
    tooltip_text: 'Process online orders',
    icon: 'ShoppingCart',
    order: 3
  },

  'ecommerce-storefronts': {
    id: 'ecommerce-storefronts',
    title: 'Storefronts',
    route: '/ecommerce/storefronts',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ECOMMERCE,
    description: 'Storefront builder with templates and customization options.',
    key_features: ['Template selection', 'Theme customization', 'Domain setup', 'SEO settings'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'ecommerce',
    children: [],
    links_to: [],
    tags: ['storefronts', 'design', 'setup'],
    status: 'active',
    tooltip_text: 'Build and customize storefronts',
    icon: 'Store',
    order: 4
  },

  // CNS Module (Social Media)
  'cns': {
    id: 'cns',
    title: 'Social Media',
    route: '/cns',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Social media management with content creation, scheduling, and analytics.',
    key_features: ['Content studio', 'Multi-channel posting', 'Analytics', 'AI content generation'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['cns-dashboard', 'cns-content', 'cns-scheduler', 'cns-channels', 'cns-campaigns', 'cns-analytics', 'cns-ai'],
    links_to: [],
    tags: ['social', 'marketing', 'operations'],
    status: 'active',
    tooltip_text: 'Social media management',
    icon: 'Share2',
    badge: 'New',
    order: 12
  },

  'cns-dashboard': {
    id: 'cns-dashboard',
    title: 'Social Dashboard',
    route: '/cns/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Social media overview with engagement metrics and posting schedule.',
    key_features: ['Engagement metrics', 'Posting schedule', 'Channel health', 'Content queue'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: [],
    tags: ['dashboard', 'analytics', 'daily'],
    status: 'active',
    tooltip_text: 'Social media performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'cns-content': {
    id: 'cns-content',
    title: 'Content Studio',
    route: '/cns/content',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Create and manage social media content with AI-assisted writing.',
    key_features: ['Content editor', 'AI generation', 'Media library', 'Templates'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: ['cns-ai'],
    tags: ['content', 'creation', 'daily'],
    status: 'active',
    tooltip_text: 'Create social content',
    icon: 'PenSquare',
    order: 2
  },

  'cns-scheduler': {
    id: 'cns-scheduler',
    title: 'Scheduler',
    route: '/cns/scheduler',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Schedule posts across multiple social media channels.',
    key_features: ['Calendar view', 'Bulk scheduling', 'Time zone support', 'Best time suggestions'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: ['calendar'],
    tags: ['scheduling', 'posts', 'daily'],
    status: 'active',
    tooltip_text: 'Schedule social posts',
    icon: 'CalendarClock',
    order: 3
  },

  'cns-channels': {
    id: 'cns-channels',
    title: 'Channels',
    route: '/cns/channels',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Connect and manage social media accounts and channels.',
    key_features: ['Account linking', 'Channel settings', 'Permissions', 'Status monitoring'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: [],
    tags: ['channels', 'accounts', 'setup'],
    status: 'active',
    tooltip_text: 'Manage social channels',
    icon: 'Share2',
    order: 4
  },

  'cns-campaigns': {
    id: 'cns-campaigns',
    title: 'Campaigns',
    route: '/cns/campaigns',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Plan and track marketing campaigns across social channels.',
    key_features: ['Campaign planning', 'Content grouping', 'Performance tracking', 'ROI analysis'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: [],
    tags: ['campaigns', 'marketing', 'reporting'],
    status: 'active',
    tooltip_text: 'Manage social campaigns',
    icon: 'Target',
    order: 5
  },

  'cns-analytics': {
    id: 'cns-analytics',
    title: 'Social Analytics',
    route: '/cns/analytics',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Deep analytics for social media performance and audience insights.',
    key_features: ['Engagement reports', 'Audience insights', 'Competitor analysis', 'Trend tracking'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: [],
    tags: ['analytics', 'reporting', 'daily'],
    status: 'active',
    tooltip_text: 'Social media analytics',
    icon: 'BarChart3',
    order: 6
  },

  'cns-ai': {
    id: 'cns-ai',
    title: 'AI Configuration',
    route: '/cns/ai',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.CNS,
    description: 'Configure AI providers for content generation (OpenAI, Anthropic, Gemini, etc.).',
    key_features: ['Provider setup', 'Model selection', 'API key management', 'Usage tracking'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'cns',
    children: [],
    links_to: [],
    tags: ['ai', 'configuration', 'setup'],
    status: 'active',
    tooltip_text: 'Configure AI for content generation',
    icon: 'Cpu',
    order: 7
  },

  // Report Builder Module
  'reports': {
    id: 'reports',
    title: 'Report Builder',
    route: '/reports',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.REPORTS,
    description: 'Enterprise report builder with scheduling, templates, and multi-format export.',
    key_features: ['Report templates', 'Scheduling', 'Multi-format export', 'Email delivery'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['reports-main', 'reports-new', 'reports-types'],
    links_to: ['finance-reports'],
    tags: ['reports', 'analytics', 'operations'],
    status: 'active',
    tooltip_text: 'Build and schedule custom reports',
    icon: 'FileBarChart',
    badge: 'New',
    order: 13
  },

  'reports-main': {
    id: 'reports-main',
    title: 'My Reports',
    route: '/reports',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.REPORTS,
    description: 'View and manage your saved reports with search and filtering.',
    key_features: ['Report list', 'Favorites', 'Scheduled reports', 'Quick execute'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'reports',
    children: [],
    links_to: [],
    tags: ['reports', 'list', 'daily'],
    status: 'active',
    tooltip_text: 'View saved reports',
    icon: 'FileText',
    order: 1
  },

  'reports-new': {
    id: 'reports-new',
    title: 'New Report',
    route: '/reports/new',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.REPORTS,
    description: 'Create new reports with drag-and-drop column selection and filtering.',
    key_features: ['Column selector', 'Filter builder', 'Preview', 'Scheduling'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'reports',
    children: [],
    links_to: [],
    tags: ['reports', 'creation', 'daily'],
    status: 'active',
    tooltip_text: 'Create a new report',
    icon: 'Plus',
    order: 2
  },

  'reports-types': {
    id: 'reports-types',
    title: 'Report Types',
    route: '/reports/types',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.REPORTS,
    description: 'Manage report types and their field configurations.',
    key_features: ['Type management', 'Field configuration', 'Permissions', 'System types'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'reports',
    children: [],
    links_to: [],
    tags: ['reports', 'configuration', 'setup'],
    status: 'active',
    tooltip_text: 'Configure report types',
    icon: 'Settings',
    order: 3
  },

  // Human Resources Module
  'hr': {
    id: 'hr',
    title: 'Human Resources',
    route: '/hr',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.HR,
    description: 'Human resources management with employees, time & attendance, leave management, and payroll processing.',
    key_features: ['Employee management', 'Time tracking', 'Leave management', 'Payroll processing', 'Organizational structure'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['hr-dashboard', 'hr-employees', 'hr-time-attendance', 'hr-leave', 'hr-payroll', 'hr-pay-components', 'hr-shifts', 'hr-positions'],
    links_to: ['accounting'],
    tags: ['hr', 'employees', 'payroll', 'operations'],
    status: 'active',
    tooltip_text: 'Human resources management',
    icon: 'Users',
    order: 12
  },

  'hr-dashboard': {
    id: 'hr-dashboard',
    title: 'HR Dashboard',
    route: '/hr/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'HR overview with headcount, new hires, absences, and payroll status.',
    key_features: ['Headcount metrics', 'Department breakdown', 'Pending approvals', 'Payroll status'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: [],
    tags: ['dashboard', 'hr', 'daily'],
    status: 'active',
    tooltip_text: 'HR performance overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'hr-employees': {
    id: 'hr-employees',
    title: 'Employees',
    route: '/hr/employees',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Employee directory with profiles, contracts, and organizational structure.',
    key_features: ['Employee records', 'Contract management', 'Org chart', 'Document management'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['hr-payroll'],
    tags: ['employees', 'hr', 'daily'],
    status: 'active',
    tooltip_text: 'Manage employees',
    icon: 'UserCircle',
    order: 2
  },

  'hr-time-attendance': {
    id: 'hr-time-attendance',
    title: 'Time & Attendance',
    route: '/hr/time-attendance',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Time tracking with clock in/out, timesheets, and overtime management.',
    key_features: ['Clock in/out', 'Timesheet approval', 'Overtime tracking', 'Shift management'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['hr-payroll'],
    tags: ['time', 'attendance', 'daily'],
    status: 'active',
    tooltip_text: 'Track time and attendance',
    icon: 'Clock',
    order: 3
  },

  'hr-leave': {
    id: 'hr-leave',
    title: 'Leave Management',
    route: '/hr/leave',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Leave requests, approvals, and balance tracking.',
    key_features: ['Leave requests', 'Approval workflow', 'Balance tracking', 'Calendar view'],
    permissions_required: [PERMISSION_ROLES.OPERATOR],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: [],
    tags: ['leave', 'hr', 'daily'],
    status: 'active',
    tooltip_text: 'Manage leave requests',
    icon: 'CalendarOff',
    order: 4
  },

  'hr-payroll': {
    id: 'hr-payroll',
    title: 'Payroll',
    route: '/hr/payroll',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Payroll processing with calculation, approval, and posting to accounting.',
    key_features: ['Payroll runs', 'Calculation engine', 'Approval workflow', 'Accounting integration'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['accounting-events'],
    tags: ['payroll', 'hr', 'monthly'],
    status: 'active',
    tooltip_text: 'Process payroll',
    icon: 'Wallet',
    order: 5
  },

  'hr-pay-components': {
    id: 'hr-pay-components',
    title: 'Pay Components',
    route: '/hr/pay-components',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Payroll component registry for earnings, deductions, taxes, and employer costs.',
    key_features: ['Earnings', 'Deductions', 'Taxes', 'Employer costs'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['hr-payroll', 'accounting-posting-rules'],
    tags: ['payroll', 'components', 'setup'],
    status: 'active',
    tooltip_text: 'Review payroll component setup',
    icon: 'Receipt',
    order: 6
  },

  'hr-shifts': {
    id: 'hr-shifts',
    title: 'Shifts',
    route: '/hr/shifts',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Shift registry used by time and attendance entries.',
    key_features: ['Shift definitions', 'Clock rules', 'Attendance setup'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['hr-time-attendance'],
    tags: ['shifts', 'time', 'setup'],
    status: 'active',
    tooltip_text: 'Review active shifts',
    icon: 'Clock',
    order: 7
  },

  'hr-positions': {
    id: 'hr-positions',
    title: 'Positions',
    route: '/hr/positions',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.HR,
    description: 'Position and role definitions by department.',
    key_features: ['Position registry', 'Department assignment', 'Role structure'],
    permissions_required: [PERMISSION_ROLES.MANAGER],
    visible_in_menu: true,
    menu_parent_id: 'hr',
    children: [],
    links_to: ['hr-employees'],
    tags: ['positions', 'departments', 'setup'],
    status: 'active',
    tooltip_text: 'Review employee positions',
    icon: 'Briefcase',
    order: 8
  },

  // Accounting Module (Ledger Backbone)
  'accounting': {
    id: 'accounting',
    title: 'Accounting',
    route: '/accounting',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'General ledger backbone with chart of accounts, journals, fiscal periods, and statutory exports.',
    key_features: ['Chart of accounts', 'Journal entries', 'Period close', 'Event inbox', 'Statutory exports'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['accounting-dashboard', 'accounting-coa', 'accounting-journals', 'accounting-events', 'accounting-periods', 'accounting-exports', 'accounting-posting-rules'],
    links_to: ['finance', 'hr-payroll'],
    tags: ['accounting', 'ledger', 'finance'],
    status: 'active',
    tooltip_text: 'General ledger and accounting',
    icon: 'BookOpen',
    order: 13
  },

  'accounting-dashboard': {
    id: 'accounting-dashboard',
    title: 'Accounting Dashboard',
    route: '/accounting/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Accounting overview with period status, posting health, and rejected events.',
    key_features: ['Period status', 'Posting health', 'Event metrics', 'Close checklist'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: [],
    tags: ['dashboard', 'accounting', 'daily'],
    status: 'active',
    tooltip_text: 'Accounting overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'accounting-coa': {
    id: 'accounting-coa',
    title: 'Chart of Accounts',
    route: '/accounting/chart-of-accounts',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Manage chart of accounts with account types, hierarchy, and control accounts.',
    key_features: ['Account hierarchy', 'Account types', 'Control accounts', 'Bank accounts'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: [],
    tags: ['coa', 'accounts', 'setup'],
    status: 'active',
    tooltip_text: 'Manage chart of accounts',
    icon: 'List',
    order: 2
  },

  'accounting-journals': {
    id: 'accounting-journals',
    title: 'Journal Entries',
    route: '/accounting/journals',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'View and create journal entries with posting, approval, and reversal capabilities.',
    key_features: ['Manual journals', 'Posting workflow', 'Journal reversal', 'Dimension tracking'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: [],
    tags: ['journals', 'accounting', 'daily'],
    status: 'active',
    tooltip_text: 'Manage journal entries',
    icon: 'FileText',
    order: 3
  },

  'accounting-events': {
    id: 'accounting-events',
    title: 'Event Inbox',
    route: '/accounting/events',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Accounting event inbox for processing events from AR, AP, HR, and other modules.',
    key_features: ['Event processing', 'Validation', 'Rejection handling', 'Event replay'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: ['accounting-journals'],
    tags: ['events', 'integration', 'daily'],
    status: 'active',
    tooltip_text: 'Process accounting events',
    icon: 'Inbox',
    order: 4
  },

  'accounting-periods': {
    id: 'accounting-periods',
    title: 'Period Close',
    route: '/accounting/periods',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Fiscal period management with close checklist and period locking.',
    key_features: ['Period status', 'Close checklist', 'Period lock', 'Reopen requests'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: [],
    tags: ['periods', 'close', 'monthly'],
    status: 'active',
    tooltip_text: 'Manage fiscal periods',
    icon: 'Calendar',
    order: 5
  },

  'accounting-exports': {
    id: 'accounting-exports',
    title: 'Statutory Exports',
    route: '/accounting/exports',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Generate statutory reports and exports based on country pack configuration.',
    key_features: ['Export definitions', 'Job runner', 'File generation', 'Audit trail'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: [],
    tags: ['exports', 'statutory', 'monthly'],
    status: 'active',
    tooltip_text: 'Generate statutory exports',
    icon: 'Download',
    order: 6
  },

  'accounting-posting-rules': {
    id: 'accounting-posting-rules',
    title: 'Posting Rules',
    route: '/accounting/posting-rules',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ACCOUNTING,
    description: 'Accounting posting rules that map operational events into journal entries.',
    key_features: ['Event mapping', 'Rule versions', 'Validation rules', 'Accounting automation'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'accounting',
    children: [],
    links_to: ['accounting-events', 'accounting-journals'],
    tags: ['posting', 'rules', 'automation', 'setup'],
    status: 'active',
    tooltip_text: 'Review event posting rules',
    icon: 'Settings',
    order: 7
  },

  // Fiscal Module
  'fiscal': {
    id: 'fiscal',
    title: 'Fiscal Engine',
    route: '/fiscal',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Brazil fiscal operations for SEFAZ documents, apuração, SPED/EFD exports, fiscal matrix rules, and audit evidence.',
    key_features: ['SEFAZ lifecycle', 'Apuração engine', 'SPED/EFD exports', 'Fiscal matrix', 'Audit evidence'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['fiscal-dashboard', 'fiscal-documents', 'fiscal-apuracao', 'fiscal-sped', 'fiscal-matrix', 'fiscal-events', 'fiscal-audit'],
    links_to: ['accounting', 'oig'],
    tags: ['fiscal', 'brazil', 'sefaz', 'sped'],
    status: 'active',
    tooltip_text: 'Brazil fiscal engine',
    icon: 'FileCheck',
    order: 14
  },

  'fiscal-dashboard': {
    id: 'fiscal-dashboard',
    title: 'Fiscal Dashboard',
    route: '/fiscal/dashboard',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Brazil fiscal overview for SEFAZ document status, apuração readiness, SPED/EFD exports, and audit controls.',
    key_features: ['Fiscal KPIs', 'Lifecycle status', 'Quick actions', 'Audit readiness'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['fiscal-documents', 'fiscal-apuracao', 'fiscal-sped'],
    tags: ['dashboard', 'fiscal', 'daily'],
    status: 'active',
    tooltip_text: 'Fiscal overview',
    icon: 'LayoutDashboard',
    order: 1
  },

  'fiscal-documents': {
    id: 'fiscal-documents',
    title: 'Fiscal Documents',
    route: '/fiscal/documents',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Transmit fiscal documents and poll SEFAZ authorization status using the fiscal backend endpoints.',
    key_features: ['Transmit document', 'Poll SEFAZ status', 'Authorization result', 'Error payload visibility'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['fiscal-events'],
    tags: ['sefaz', 'documents', 'authorization'],
    status: 'active',
    tooltip_text: 'Manage fiscal document lifecycle',
    icon: 'FileCheck',
    order: 2
  },

  'fiscal-apuracao': {
    id: 'fiscal-apuracao',
    title: 'Apuração',
    route: '/fiscal/apuracao',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Run Brazilian fiscal period apuração for authorized documents and period tax calculation.',
    key_features: ['ICMS', 'PIS/COFINS', 'Period calculation', 'Close evidence'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['accounting-periods', 'fiscal-sped'],
    tags: ['apuracao', 'tax', 'period'],
    status: 'active',
    tooltip_text: 'Run fiscal apuração',
    icon: 'Calculator',
    order: 3
  },

  'fiscal-sped': {
    id: 'fiscal-sped',
    title: 'SPED / EFD',
    route: '/fiscal/sped',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Generate Brazilian SPED/EFD statutory fiscal export files from the fiscal backbone.',
    key_features: ['EFD Contribuições', 'SPED export', 'Output metadata', 'Audit trace'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['accounting-exports'],
    tags: ['sped', 'efd', 'exports'],
    status: 'active',
    tooltip_text: 'Generate fiscal exports',
    icon: 'FileSpreadsheet',
    order: 4
  },

  'fiscal-matrix': {
    id: 'fiscal-matrix',
    title: 'Fiscal Matrix',
    route: '/fiscal/matrix',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Fiscal rule matrix dimensions for Brazilian tax determination and accounting handoff.',
    key_features: ['UF', 'NCM', 'CFOP', 'CST/CSOSN', 'Regime rules'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['fiscal-documents', 'accounting-posting-rules'],
    tags: ['matrix', 'rules', 'tax'],
    status: 'active',
    tooltip_text: 'Review fiscal rule matrix',
    icon: 'Database',
    order: 5
  },

  'fiscal-events': {
    id: 'fiscal-events',
    title: 'Fiscal Events',
    route: '/fiscal/events',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Fiscal webhook and lifecycle event visibility for document and statutory processing.',
    key_features: ['Webhook', 'Lifecycle events', 'Retry context', 'Failure visibility'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['accounting-events'],
    tags: ['events', 'webhook', 'fiscal'],
    status: 'active',
    tooltip_text: 'Review fiscal events',
    icon: 'Activity',
    order: 6
  },

  'fiscal-audit': {
    id: 'fiscal-audit',
    title: 'Audit Evidence',
    route: '/fiscal/audit',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.FISCAL,
    description: 'Audit evidence map for SEFAZ protocols, apuração records, SPED/EFD export outputs, and fiscal close controls.',
    key_features: ['Protocol evidence', 'Export evidence', 'Period controls', 'Duplicate guards'],
    permissions_required: [PERMISSION_ROLES.FINANCE],
    visible_in_menu: true,
    menu_parent_id: 'fiscal',
    children: [],
    links_to: ['fiscal-sped', 'accounting-exports'],
    tags: ['audit', 'evidence', 'control'],
    status: 'active',
    tooltip_text: 'Review fiscal audit evidence',
    icon: 'Shield',
    order: 7
  },

  // Admin Module
  'admin': {
    id: 'admin',
    title: 'Administration',
    route: '/admin',
    type: NODE_TYPES.GROUP,
    module_owner: MODULE_OWNERS.ADMIN,
    description: 'System administration including users, roles, settings, and help center.',
    key_features: ['User management', 'Role configuration', 'System settings', 'Help center'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'root',
    children: ['admin-users', 'admin-roles', 'admin-settings', 'admin-help'],
    links_to: [],
    tags: ['admin', 'settings', 'setup'],
    status: 'active',
    tooltip_text: 'System administration',
    icon: 'Settings',
    order: 15
  },

  'admin-users': {
    id: 'admin-users',
    title: 'User Management',
    route: '/admin/users',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ADMIN,
    description: 'Manage user accounts, permissions, and access profiles.',
    key_features: ['User list', 'Role assignment', 'Access profiles', 'Activity log'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'admin',
    children: [],
    links_to: ['admin-roles'],
    tags: ['users', 'security', 'setup'],
    status: 'active',
    tooltip_text: 'Manage users',
    icon: 'Users',
    order: 1
  },

  'admin-roles': {
    id: 'admin-roles',
    title: 'Access Profiles',
    route: '/admin/access-profiles',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ADMIN,
    description: 'Configure roles, permission profiles, and user access monitoring for the organization.',
    key_features: ['Role definition', 'Permission sets', 'Module access', 'Grant rules', 'User Access Monitoring', 'Last Activity', 'Login Time', 'Logout Time'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'admin',
    children: [],
    links_to: ['admin-users'],
    tags: ['roles', 'permissions', 'setup', 'user-monitoring', 'last-activity', 'access-monitoring'],
    status: 'active',
    tooltip_text: 'Configure access profiles',
    icon: 'Shield',
    order: 2
  },

  'admin-settings': {
    id: 'admin-settings',
    title: 'Settings',
    route: '/settings',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ADMIN,
    description: 'System-wide settings and configuration options.',
    key_features: ['General settings', 'Email config', 'Integrations', 'Branding'],
    permissions_required: [PERMISSION_ROLES.CLIENT_ADMIN],
    visible_in_menu: true,
    menu_parent_id: 'admin',
    children: [],
    links_to: [],
    tags: ['settings', 'configuration', 'setup'],
    status: 'active',
    tooltip_text: 'System settings',
    icon: 'Settings',
    order: 3
  },

  'admin-help': {
    id: 'admin-help',
    title: 'Help Center',
    route: '/admin/help-center',
    type: NODE_TYPES.PAGE,
    module_owner: MODULE_OWNERS.ADMIN,
    description: 'Documentation, guides, and system information.',
    key_features: ['Documentation', 'API reference', 'Visual mapping', 'Setup guides'],
    permissions_required: [PERMISSION_ROLES.VIEWER],
    visible_in_menu: true,
    menu_parent_id: 'admin',
    children: [],
    links_to: [],
    tags: ['help', 'documentation', 'support'],
    status: 'active',
    tooltip_text: 'Help and documentation',
    icon: 'HelpCircle',
    order: 4
  }
};

// Helper functions to derive views from SSOT

/**
 * Get all nodes as a flat array
 */
export const getAllNodes = () => Object.values(NAVIGATION_SSOT);

/**
 * Get a node by ID
 */
export const getNodeById = (id) => NAVIGATION_SSOT[id];

/**
 * Get children of a node
 */
export const getNodeChildren = (nodeId) => {
  const node = NAVIGATION_SSOT[nodeId];
  if (!node || !node.children) return [];
  return node.children.map(childId => NAVIGATION_SSOT[childId]).filter(Boolean);
};

/**
 * Get parent chain for breadcrumb generation
 */
export const getBreadcrumbChain = (nodeId) => {
  const chain = [];
  let currentId = nodeId;

  while (currentId) {
    const node = NAVIGATION_SSOT[currentId];
    if (!node) break;

    // Check if this node should be hidden in breadcrumb
    const targetNode = NAVIGATION_SSOT[nodeId];
    const hiddenParents = targetNode?.breadcrumb_hidden_parent_ids || [];

    if (!hiddenParents.includes(currentId)) {
      chain.unshift({
        id: node.id,
        title: node.breadcrumb_label || node.title,
        route: node.route
      });
    }

    currentId = node.menu_parent_id;
  }

  return chain;
};

/**
 * Get visible menu nodes only
 */
export const getMenuNodes = () => {
  return getAllNodes()
    .filter(node => node.visible_in_menu)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Get nodes by module owner
 */
export const getNodesByModule = (moduleOwner) => {
  return getAllNodes().filter(node => node.module_owner === moduleOwner);
};

/**
 * Get nodes by type
 */
export const getNodesByType = (type) => {
  return getAllNodes().filter(node => node.type === type);
};

/**
 * Get nodes by tag
 */
export const getNodesByTag = (tag) => {
  return getAllNodes().filter(node => node.tags?.includes(tag));
};

/**
 * Search nodes by title or description
 */
export const searchNodes = (query) => {
  const lowerQuery = query.toLowerCase();
  return getAllNodes().filter(node =>
    node.title.toLowerCase().includes(lowerQuery) ||
    node.description?.toLowerCase().includes(lowerQuery) ||
    node.key_features?.some(f => f.toLowerCase().includes(lowerQuery)) ||
    node.tags?.some(t => t.toLowerCase().includes(lowerQuery))
  );
};

/**
 * Get click path to a node
 */
export const getClickPath = (nodeId) => {
  const chain = getBreadcrumbChain(nodeId);
  return chain.map(item => item.title).join(' → ');
};

/**
 * Build tree structure from flat SSOT
 */
export const buildTree = (rootId = 'root') => {
  const buildNodeTree = (nodeId) => {
    const node = NAVIGATION_SSOT[nodeId];
    if (!node) return null;

    return {
      ...node,
      childNodes: (node.children || [])
        .map(childId => buildNodeTree(childId))
        .filter(Boolean)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    };
  };

  return buildNodeTree(rootId);
};

/**
 * Build menu tree (visible nodes only)
 */
export const buildMenuTree = (rootId = 'root') => {
  const buildNodeTree = (nodeId) => {
    const node = NAVIGATION_SSOT[nodeId];
    if (!node) return null;

    const childNodes = (node.children || [])
      .map(childId => buildNodeTree(childId))
      .filter(n => n && n.visible_in_menu)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (!node.visible_in_menu && childNodes.length === 0) {
      return null;
    }

    return {
      ...node,
      childNodes
    };
  };

  const tree = buildNodeTree(rootId);
  return tree?.childNodes || [];
};

export default NAVIGATION_SSOT;
